import { cn } from '@bridebook/aura';
import { IWedding_Website_Section_PhotosField } from '@bridebook/models/source/models/Weddings.types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { ImageX } from '@bridebook/ui';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/floating-edit-button';
import { CarouselDots } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/carousel-dots';
import {
  AUTOPLAY_INTERVAL,
  CAROUSEL_IMAGE,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/constants';
import { useEmblaWithPlugins } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/hooks/use-embla-with-plugins';

type PhotoData = IWedding_Website_Section_PhotosField['data'][number];

type AnimationType = 'fade' | 'slide';

/** Mobile carousel: fixed 375px height */
const MOBILE_CONTAINER_HEIGHT = 375;

/** Image request size - portrait ratio to minimize cropping of portrait photos */
const MOBILE_IMAGE = {
  width: 375,
  height: 500,
} as const;

type Props = {
  photos: PhotoData[];
  activeIndex: number;
  onIndexChange?: (index: number) => void;
  className?: string;
  variant: 'desktop' | 'mobile';
  autoplay?: boolean;
  autoplayInterval?: number;
  /** Animation style for transitions. 'slide' shows directional slide animation. Default: 'fade' */
  animation?: AnimationType;
};

export const PhotoCarousel = ({
  photos,
  activeIndex,
  onIndexChange,
  className,
  variant,
  autoplay = false,
  autoplayInterval = AUTOPLAY_INTERVAL.default,
  animation = 'fade',
}: Props) => {
  const isInteractive = variant === 'mobile' && !!onIndexChange;
  const withFade = animation === 'fade';

  const { emblaRef, scrollTo } = useEmblaWithPlugins({
    loop: true,
    autoplay: isInteractive && autoplay,
    autoplayDelay: autoplayInterval,
    fade: withFade,
    onSelect: isInteractive ? onIndexChange : undefined,
    controlledIndex: !isInteractive ? activeIndex : undefined,
    enabled: isInteractive,
  });

  if (photos.length === 0) return null;

  const handleDotClick = isInteractive ? scrollTo : undefined;
  const isMobile = variant === 'mobile';
  const imageConfig = isMobile ? MOBILE_IMAGE : CAROUSEL_IMAGE.desktop;

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden',
        isMobile ? 'bg-[var(--ww-palette-surface-primary)]' : 'h-full',
        className,
      )}
      style={isMobile ? { height: MOBILE_CONTAINER_HEIGHT } : undefined}>
      {/* Skeleton background - visible while image loads */}
      <div className="bg-space-10 absolute inset-0 animate-pulse" />

      <div ref={emblaRef} className="relative h-full w-full overflow-hidden">
        <div className={cn('flex h-full', withFade && 'embla__container--fade')}>
          {photos.map((photo, index) => (
            <div
              key={photo.path}
              className="flex h-full w-full min-w-0 flex-[0_0_100%] items-center justify-center">
              <ImageX
                src={`${IMAGES_BASE_URL}/${photo.path}`}
                w={imageConfig.width}
                h={imageConfig.height}
                fit="crop"
                loading={index <= 1 ? 'eager' : 'lazy'}
                customParams={{ gravity: 'face' }}
                classNames={{
                  wrapper: 'h-full w-full',
                  img: 'h-full w-full object-cover',
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {isInteractive && photos.length > 1 && (
        <CarouselDots count={photos.length} activeIndex={activeIndex} onDotClick={handleDotClick} />
      )}

      <FloatingEditButton detailsSectionId="photos" />
    </div>
  );
};
